import { REACT_APP_BE_API_STATS_URL } from '#shared';

import { fetchWithAuthHeaders } from '../fetchWithAuthHeaders';

import type {
  StatsAOGDRAircraft,
  StatsAOGDRSummary,
  StatsDowntimeAircraft,
  StatsDowntimeSummary,
  StatsFHAircraft,
  StatsFHSummary,
  StatsFRAircraft,
  StatsFRSummary,
  StatsMTBAAircraft,
  StatsMTBASummary,
  StatsMTTRAircraft,
  StatsMTTRSummary,
  StatsUptimeAircraft,
  StatsUptimeSummary,
} from '#entities';
import type { StatsQueryParams } from './queries';

// Centralized mapping of backend stats API endpoints
const paths = {
  aircraft: 'api/stats/aircrafts',
  aogdr: 'api/stats/aogdr',
  downtime: 'api/stats/downtime',
  fh: 'api/stats/fh',
  fr: 'api/stats/fr',
  mtba: 'api/stats/mtba',
  mttr: 'api/stats/mttr',
  uptime: 'api/stats/uptime',
  grantedGroups: 'api/granted-groups',
} as const;

// Generic fetcher for all stats endpoints
const fetchStats = async (path: string, json = true) => {
  const url = `${REACT_APP_BE_API_STATS_URL}${path}`;
  return fetchWithAuthHeaders(url, json);
};

// Builds query string for stats endpoints using shared parameters
const buildStatsQuery = ({
  dateTo,
  fromValue,
  fromUnitType,
  aircraftsIdList,
}: StatsQueryParams) =>
  new URLSearchParams({
    dateTo: dateTo.toISOString(),
    fromValue: `${fromValue}`,
    fromUnitType,
    aircrafts: (aircraftsIdList ?? []).toString(),
  }).toString();

// Shared fetch helper for stats queries with parameters
const fetchStatWithQuery = async <T>(
  path: string,
  params: StatsQueryParams,
): Promise<T> => {
  const query = buildStatsQuery(params);
  return fetchStats(`${path}?${query}`);
};

// Exported API object with stat-specific fetchers
export const statsApi = {
  getAircraft: async () => fetchStats(paths.aircraft),
  getDowntime: async params =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsDowntimeSummary>;
      summary: StatsDowntimeSummary;
      perAircraft: Record<string, StatsDowntimeAircraft>;
    }>(paths.downtime, params),
  getFh: async params =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsFHSummary>;
      summary: StatsFHSummary;
      perAircraft: Record<string, StatsFHAircraft>;
    }>(paths.fh, params),
  getFr: async params =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsFRSummary>;
      summary: StatsFRSummary;
      perAircraft: Record<string, StatsFRAircraft>;
    }>(paths.fr, params),
  getMtba: async (params: StatsQueryParams) =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsMTBASummary>;
      summary: StatsMTBASummary;
      perAircraft: Record<string, StatsMTBAAircraft>;
    }>(paths.mtba, params),
  getMttr: async (params: StatsQueryParams) =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsMTTRSummary>;
      summary: StatsMTTRSummary;
      perAircraft: Record<string, StatsMTTRAircraft>;
    }>(paths.mttr, params),
  getUptime: async (params: StatsQueryParams) =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsUptimeSummary>;
      summary: StatsUptimeSummary;
      perAircraft: Record<string, StatsUptimeAircraft>;
    }>(paths.uptime, params),
  getAogdr: async (params: StatsQueryParams) =>
    fetchStatWithQuery<{
      perDate: Record<string, StatsAOGDRSummary>;
      summary: StatsAOGDRSummary;
      perAircraft: Record<string, StatsAOGDRAircraft>;
    }>(paths.aogdr, params),

  // Special case: returns JSON-encoded header string from response
  getGrantedGroups: async () => {
    const res = await fetchStats(paths.grantedGroups, false);
    const userGroups = res.headers.get('user-groups');
    return JSON.parse(userGroups ?? '[]');
  },
};
